/**
 * Microphone permission utility functions
 * Check and monitor microphone permission status using the Permissions API
 */

export type MicrophonePermissionStatus = 'granted' | 'denied' | 'prompt';

/**
 * Check microphone permission status
 * @returns Promise resolving to the current microphone permission status
 */
export async function checkMicrophonePermission(): Promise<MicrophonePermissionStatus> {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return result.state as MicrophonePermissionStatus;
    }
  } catch (error) {
    console.error('Error checking microphone permission:', error);
  }
  // Fallback: if Permissions API not supported, assume prompt
  return 'prompt';
}

/**
 * Check if microphone permission is granted
 * @returns Promise resolving to true if microphone permission is granted
 */
export async function isMicrophoneGranted(): Promise<boolean> {
  const permission = await checkMicrophonePermission();
  return permission === 'granted';
}

/**
 * Check if microphone permission needs to be prompted
 * @returns Promise resolving to true if permission needs to be prompted
 */
export async function needsMicrophonePrompt(): Promise<boolean> {
  const permission = await checkMicrophonePermission();
  return permission === 'prompt';
}

/**
 * Verify microphone access from a MediaStream
 * @param stream MediaStream from getUserMedia
 * @returns true if microphone access is properly granted and stream is active
 */
export function verifyMicrophoneAccess(stream: MediaStream): boolean {
  const audioTracks = stream.getAudioTracks();
  
  if (audioTracks.length === 0) {
    return false;
  }
  
  const micTrack = audioTracks[0];
  return micTrack.enabled && 
         micTrack.readyState === 'live' && 
         stream.active;
}

/**
 * Monitor microphone permission changes
 * @param callback Function to call when permission status changes
 * @returns Function to stop monitoring
 */
export function monitorMicrophonePermission(
  callback: (status: MicrophonePermissionStatus) => void
): () => void {
  let permissionStatus: PermissionStatus | null = null;

  const startMonitoring = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        callback(permissionStatus.state as MicrophonePermissionStatus);

        const handlePermissionChange = () => {
          if (permissionStatus) {
            callback(permissionStatus.state as MicrophonePermissionStatus);
          }
        };

        permissionStatus.addEventListener('change', handlePermissionChange);

        return () => {
          if (permissionStatus) {
            permissionStatus.removeEventListener('change', handlePermissionChange);
          }
        };
      }
    } catch (error) {
      console.error('Error monitoring microphone permission:', error);
    }
    return () => {}; // No-op cleanup function
  };

  let cleanup: (() => void) | null = null;

  startMonitoring().then((cleanupFn) => {
    cleanup = cleanupFn;
  });

  return () => {
    if (cleanup) {
      cleanup();
    }
  };
}

